// Shared recharts axis config for `CategoryAxis` / `ValueAxis`.
// Orientation-independent: a title belongs to a *logical* axis (category
// vs value); which physical recharts axis carries it is decided by the
// orientation model. The category axis is bottom-titled in one
// orientation and side-titled in the other (and vice-versa for the
// value axis), so the title factories — and the chrome both axes share
// — live here, not inside either component.

import type { ReactNode } from 'react';
import type { LabelProps } from 'recharts';

import { CHART_SHADE, mantineColorVar } from '#core/lib/formatters/colors.ts';
import { DATE_FORMAT_PATTERN } from '#core/lib/utils/date.ts';

import { AxisTitle } from './AxisTitle.tsx';

const MINUTE_MS = 60 * 1000;
const DAY_MS = 24 * 60 * MINUTE_MS;
const YEAR_MS = 365 * DAY_MS;
/**
 * Adaptive `dayjs` format pattern for a time-axis tick label, picked
 * from the *visible range* (max−min in ms) of the data. Coarse ranges
 * collapse to year/year-month; sub-day ranges show time-of-day. Used
 * by the cartesian panel's category-axis tick formatter so a chart
 * spanning years doesn't render `'YY-MM-DD'` ticks (cluttered) and a
 * sub-day chart doesn't render bare dates (uninformative). Tooltip
 * labels use `pickTooltipDateFormat` instead — a tooltip must be
 * maximally precise to identify a single datapoint, regardless of
 * how wide the chart's range is.
 */
export const pickDateFormat = (rangeMs: number): string => {
  if (rangeMs > 2 * YEAR_MS) {
    return 'YYYY';
  }
  if (rangeMs > 180 * DAY_MS) {
    return 'MMM YY';
  }
  if (rangeMs > 2 * DAY_MS) {
    return 'MMM D';
  }
  if (rangeMs > DAY_MS) {
    return 'MMM D, HH:mm';
  }
  return 'HH:mm';
};

/**
 * Maximum-precision `dayjs` format pattern for a tooltip label, picked
 * from the *finest delta* between consecutive sorted x values. The
 * tooltip identifies a single data point, so it must always include
 * enough precision to distinguish neighbours — opposite of
 * `pickDateFormat`, which is tuned for axis-tick density. Empty /
 * single-point inputs fall back to minute precision (defensive — we
 * can't infer regularity from a single sample).
 */
export const pickTooltipDateFormat = (xValues: readonly number[]): string => {
  const sorted = [...xValues].sort((a, b) => a - b);
  // `i` ranges over indices of `sorted.slice(1)`, so `sorted[i]` is
  // always the element immediately before `v` and is guaranteed to
  // exist — the assertion satisfies `noUncheckedIndexedAccess`.
  const deltas = sorted
    .slice(1)
    .map((v, i) => v - (sorted[i] as number))
    .filter((d) => d > 0);
  if (deltas.length === 0) {
    return 'YYYY-MM-DD HH:mm';
  }
  const minDelta = Math.min(...deltas);
  if (minDelta < MINUTE_MS) {
    return 'YYYY-MM-DD HH:mm:ss';
  }
  if (minDelta < DAY_MS) {
    return 'YYYY-MM-DD HH:mm';
  }
  return DATE_FORMAT_PATTERN;
};
/**
 * Shape of a label formatter consumed by recharts axis ticks and
 * tooltip labels. `value` is widened to `ReactNode | undefined`
 * because recharts hands ReactNode-shaped tick values in some paths
 * (e.g. percent-stack); the formatter must coerce anything that
 * isn't a primitive into a fallback string. Defined here because
 * `axisLabels.ts` is the home for both creators (`pickDateFormat`,
 * `pickTooltipDateFormat`) and the formatters they parameterise.
 */
export type LabelFormatter = (value: number | string | ReactNode | undefined) => string;

/** Chrome shared by every axis (tick text, axis line, stroke). */
export const AXIS_CHROME = {
  axisLine: false,
  fontSize: 12,
  fontWeight: 500,
  stroke: mantineColorVar('dark', CHART_SHADE.axisChrome),
} as const;

/**
 * Shared axis-title label config: a custom SVG `AxisTitle` renderer for the
 * given placement, or `undefined` when there is nothing to show. Bottom vs
 * rotated is now purely the placement string — both axes render their title
 * through the same two-tone renderer, with horizontal space reserved by the
 * chart margin (`AXIS_TITLE_RESERVE`) for rotated placements.
 */
const axisTitleLabel = (placement: 'bottom' | 'left' | 'right', text: string | undefined, unit?: string) => {
  if (text === undefined && (unit === undefined || unit === '')) {
    return undefined;
  }
  return {
    content: (props: LabelProps) => (
      <AxisTitle placement={placement} title={text ?? ''} unit={unit ?? ''} viewBox={props.viewBox} />
    ),
  };
};
/** Title anchored under a bottom (horizontal-layout X) axis, with an optional dimmed unit suffix. */
export const bottomTitle = (text: string | undefined, unit?: string) => axisTitleLabel('bottom', text, unit);

/** Rotated title anchored on the left/right of a side (Y) axis, with an optional dimmed unit suffix. */
export const sideTitle = (text: string | undefined, side: 'left' | 'right', unit?: string) =>
  axisTitleLabel(side, text, unit);
